/**
 * 自动保存队列
 * -----------------------------------------------------------------------------
 * 报名的整条链路都该「填了就在」——学员不需要知道「存草稿」这回事。
 * 但直接把每次输入都打成一个 `PATCH` 会出三类问题，所以这里做四件事：
 *
 *   ① **合并**：停手 `delayMs` 后才发一次。输入姓名 5 个字不会发 5 次请求。
 *   ② **去重**：与「已保存的那份」逐字节相同就跳过 —— 切步骤、开关弹窗
 *      都会重算 `payload`，不去重等于每次都白发一次。
 *   ③ **串行**：同一时刻只有一个请求在途。服务端草稿保存带乐观锁
 *      （`version` 比对，冲突 → `STATE_CONFLICT` 409），并发发两个必然有一个失败。
 *      在途期间到来的新值**不丢弃**，等这一轮结束再补发一次。
 *   ④ **失败不抛**：弱网下自动保存会连续失败，抛出去会变成一串 toast 把人挡在
 *      输入框外面。这里只把状态降级成 `error`，下次输入或 `flush()` 时重试。
 *      失败后**不自动重试** —— 否则断网时就是一个「失败→立刻重试→失败」的循环。
 *
 * 刻意不做的事：
 *   - 不管「保存什么」（由调用方决定传什么），也不管本地暂存（那是 `store/draft`）；
 *   - 不做 backoff（自动保存的价值在「下一次输入就补上」，退避反而拉长丢失窗口）。
 *
 * 用法：
 * ```ts
 * const queue = createSaveQueue({ save: (value) => saveDraft(id, value), onChange: setState });
 * queue.markSaved(initial);   // 以加载到的那份为基线，避免刚进页面就存一次
 * queue.push(next);           // 每次变更
 * await queue.flush();        // 切步骤 / 页面隐藏 / 提交前
 * ```
 */

/** 保存状态（界面按它渲染「保存中… / 已保存 18:42 / 未保存」） */
export type SaveState = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export interface SaveQueueOptions<T> {
  /** 停手多久后发请求（默认 1500ms） */
  delayMs?: number;
  /** 真正的保存动作；抛错会被接住，不影响调用方 */
  save: (value: T) => Promise<void>;
  /** 状态变化回调（`at` 是最近一次**成功**保存的时间戳，失败时保留上一次的） */
  onChange?: (state: SaveState, at: number | null) => void;
  /** 可注入的时钟（测试用） */
  now?: () => number;
}

export interface SaveQueue<T> {
  /** 记一次变更：重置静默期，到期后发一次 */
  push: (value: T) => void;
  /** 立刻保存（不等静默期）。失败不抛，只反映在状态上 */
  flush: () => Promise<void>;
  /** 把某个值登记为「已保存」的基线（初次加载时用） */
  markSaved: (value: T) => void;
  /** 当前状态 */
  state: () => SaveState;
  /** 最近一次成功保存的时间（无则 null） */
  savedAt: () => number | null;
  /** 停掉定时器（组件卸载时调用；在途请求不受影响） */
  dispose: () => void;
}

export function createSaveQueue<T>(options: SaveQueueOptions<T>): SaveQueue<T> {
  const delay = Math.max(0, options.delayMs ?? 1500);
  const now = options.now ?? (() => Date.now());

  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: { value: T; serialized: string } | null = null;
  let lastSaved: string | null = null;
  let inFlight: Promise<void> | null = null;
  let savedAt: number | null = null;
  let state: SaveState = 'idle';
  let disposed = false;

  function emit(next: SaveState): void {
    if (state === next) return;
    state = next;
    options.onChange?.(next, savedAt);
  }

  function clearTimer(): void {
    if (timer === null) return;
    clearTimeout(timer);
    timer = null;
  }

  function schedule(ms: number): void {
    clearTimer();
    timer = setTimeout(() => {
      timer = null;
      void exec();
    }, ms);
  }

  /** 发一次（若期间又有新值，结束后补发；失败则停手等下一次触发） */
  async function execOnce(): Promise<void> {
    if (!pending) return;
    const job = pending;
    // 与已保存内容相同：不浪费一次请求（切步骤时会走到这里）
    if (job.serialized === lastSaved) {
      pending = null;
      emit('saved');
      return;
    }

    pending = null;
    emit('saving');
    let failed = false;
    try {
      await options.save(job.value);
      lastSaved = job.serialized;
      savedAt = now();
      emit('saved');
    } catch {
      failed = true;
      // 失败的值放回去等重试，但**不能覆盖**失败期间用户新输入的内容
      if (!pending) pending = job;
      emit('error');
    }

    // 失败后不自动重试（断网时会变成失败风暴）；有更新的值则继续
    if (!failed && pending !== null && !disposed) schedule(delay);
  }

  async function exec(): Promise<void> {
    // 串行：在途时先等它结束，再判断是否需要补发
    if (inFlight !== null) await inFlight;
    if (disposed || pending === null) return;

    const task = execOnce();
    inFlight = task;
    try {
      await task;
    } finally {
      if (inFlight === task) inFlight = null;
    }
  }

  return {
    push(value: T): void {
      if (disposed) return;
      const serialized = JSON.stringify(value);
      const sameAsSaved = serialized === lastSaved;

      if (sameAsSaved) {
        // 值退回到已保存的那份（例如撤销了输入）：不用发，也不必留 pending
        pending = null;
        clearTimer();
        emit('saved');
        return;
      }

      pending = { value, serialized };
      emit('pending');
      schedule(delay);
    },

    async flush(): Promise<void> {
      if (disposed) return;
      clearTimer();
      if (inFlight !== null) await inFlight;
      if (pending !== null) await exec();
    },

    markSaved(value: T): void {
      lastSaved = JSON.stringify(value);
      pending = null;
      clearTimer();
      if (state === 'idle' || state === 'pending') emit('saved');
    },

    state: () => state,
    savedAt: () => savedAt,

    dispose(): void {
      disposed = true;
      clearTimer();
    },
  };
}
